import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  Sparkles,
  Save,
  Loader2,
  FileText,
  Upload,
  Eraser,
  Download,
  GitCompareArrows,
} from "lucide-react";
import {
  generateReportDraft,
  patchExamination,
  downloadPdf,
} from "@/lib/api";

/**
 * ReportDraftPanel — Rule-based Indonesian radiology report draft editor
 * with radiologist name + signature capture, and PDF export.
 */
export default function ReportDraftPanel({
  exam,
  onExamUpdate,
  priorList = [],
  selectedPriorId,
  onOpenComparison,
}) {
  const [draft, setDraft] = useState(exam?.report_draft || "");
  const [radName, setRadName] = useState(exam?.radiologist_name || "");
  const [signature, setSignature] = useState(exam?.radiologist_signature || "");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [expanded, setExpanded] = useState(Boolean(exam?.report_draft));
  const fileRef = useRef(null);

  useEffect(() => {
    setDraft(exam?.report_draft || "");
    setRadName(exam?.radiologist_name || "");
    setSignature(exam?.radiologist_signature || "");
  }, [exam?.id]);

  const doGenerate = async () => {
    setGenerating(true);
    try {
      const { draft: text } = await generateReportDraft(exam.id, {
        compare_with_exam_id: selectedPriorId || null,
        persist: false,
      });
      setDraft(text);
      setExpanded(true);
      toast.success("Draft laporan berhasil di-generate");
    } catch (e) {
      toast.error("Gagal generate draft", {
        description: String(e?.response?.data?.detail || e.message).slice(0, 200),
      });
    } finally {
      setGenerating(false);
    }
  };

  const doSave = async () => {
    setSaving(true);
    try {
      await patchExamination(exam.id, {
        report_draft: draft,
        radiologist_name: radName,
        radiologist_signature: signature,
      });
      toast.success("Draft laporan tersimpan");
      onExamUpdate?.();
    } catch (e) {
      toast.error("Gagal menyimpan draft");
    } finally {
      setSaving(false);
    }
  };

  const onSignatureFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Ukuran file signature maksimum 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setSignature(reader.result);
    reader.readAsDataURL(f);
  };

  const doDownload = async () => {
    // Save first so PDF reflects latest edits
    setDownloading(true);
    try {
      await patchExamination(exam.id, {
        report_draft: draft,
        radiologist_name: radName,
        radiologist_signature: signature,
      });
      await downloadPdf(exam.id, {
        compareWith: selectedPriorId || null,
        includeReport: true,
      });
      toast.success("PDF laporan diunduh");
      onExamUpdate?.();
    } catch (e) {
      toast.error("Gagal export PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-zinc-900 bg-zinc-900/40 overflow-hidden"
      data-testid="report-draft-panel"
    >
      <div className="px-5 py-4 border-b border-zinc-900 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-indigo-400" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
              Draft Laporan Radiologi
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">
              Template rule-based (Indonesia) · Editable
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {priorList.length > 0 && (
            <button
              type="button"
              onClick={onOpenComparison}
              data-testid="open-comparison-inline-btn"
              className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-950 border border-zinc-800 hover:border-zinc-700 text-zinc-200 px-3 py-1.5 text-xs font-medium"
            >
              <GitCompareArrows className="w-3.5 h-3.5" />
              Bandingkan
            </button>
          )}
          <button
            type="button"
            onClick={doGenerate}
            disabled={generating}
            data-testid="generate-report-btn"
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white px-3 py-1.5 text-xs font-medium"
          >
            {generating ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" />
            )}
            {draft ? "Regenerate" : "Generate Draft"}
          </button>
        </div>
      </div>

      {!expanded && !draft ? (
        <div className="p-6 text-sm text-zinc-500 text-center">
          Belum ada draft. Klik <span className="text-indigo-400 font-medium">Generate Draft</span>{" "}
          untuk membuat narasi laporan otomatis berdasarkan temuan
          {selectedPriorId ? " + perbandingan dengan pemeriksaan sebelumnya" : ""}.
        </div>
      ) : (
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5">
              Narasi Laporan (editable)
            </label>
            <textarea
              data-testid="report-draft-textarea"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={18}
              className="block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none resize-y font-mono leading-relaxed"
              placeholder="Klik Generate Draft di kanan atas untuk mengisi..."
            />
          </div>

          {/* Radiologist identity + signature */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-zinc-900">
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5">
                Nama Radiolog
              </label>
              <input
                data-testid="radiologist-name-input"
                type="text"
                value={radName}
                onChange={(e) => setRadName(e.target.value)}
                placeholder="dr. Contoh, Sp.Rad"
                className="block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5">
                Tanda Tangan (PNG/JPG, transparan disarankan)
              </label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={onSignatureFile}
                  className="hidden"
                  data-testid="signature-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="upload-signature-btn"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-950 hover:border-zinc-700 text-zinc-200 px-3 py-2 text-xs font-medium"
                >
                  <Upload className="w-3.5 h-3.5" />
                  {signature ? "Ganti" : "Upload"}
                </button>
                {signature && (
                  <>
                    <img
                      src={signature}
                      alt="signature preview"
                      className="h-10 bg-white/95 rounded px-2 border border-zinc-800"
                      data-testid="signature-preview"
                    />
                    <button
                      type="button"
                      onClick={() => setSignature("")}
                      data-testid="clear-signature-btn"
                      className="p-1.5 rounded-lg text-zinc-500 hover:text-rose-400 hover:bg-zinc-900"
                    >
                      <Eraser className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2 flex-wrap">
            <button
              type="button"
              onClick={doSave}
              disabled={saving}
              data-testid="save-report-draft-btn"
              className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-100 px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Simpan Draft
            </button>
            <button
              type="button"
              onClick={doDownload}
              disabled={downloading || !draft.trim()}
              data-testid="download-final-report-btn"
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 text-sm font-medium shadow-lg shadow-indigo-500/20 disabled:opacity-50"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export PDF Final
              {selectedPriorId ? " (dgn perbandingan)" : ""}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
